import { Link, useNavigate } from 'react-router-dom'
import { PropertyForm } from '@/components/properties'
import { useCreateProperty, useSaveImagesForNewProperty, type StagedImage } from '@/hooks'
import type { PropertyCreateData } from '@/lib/api'
import type { PropertyCreateInput } from '@/hooks'

/**
 * Convert form data to Convex input format.
 * Form uses `null` for empty values, Convex uses `undefined`.
 */
function formDataToConvex(data: PropertyCreateData): PropertyCreateInput {
  return {
    title: data.title,
    slug: data.slug,
    // Descriptions - store individual fields
    description_short: data.description_short ?? undefined,
    description_long: data.description_long ?? undefined,
    // Price
    price: data.price ?? undefined,
    price_currency: data.price_currency ?? 'EUR',
    // Location - store individual fields
    address: data.address ?? undefined,
    city: data.city ?? undefined,
    province: data.province ?? undefined,
    region: data.region ?? undefined,
    postal_code: data.postal_code ?? undefined,
    // Property details
    bedrooms: data.specs?.bedrooms ?? undefined,
    bathrooms: data.specs?.bathrooms ?? undefined,
    area_m2: data.specs?.area_m2 ?? undefined,
    status: data.status ?? 'draft',
    specs: data.specs ? {
      bedrooms: data.specs.bedrooms ?? undefined,
      bathrooms: data.specs.bathrooms ?? undefined,
      area_m2: data.specs.area_m2 ?? undefined,
      garage: data.specs.garage ?? undefined,
      garage_spaces: data.specs.garage_spaces ?? undefined,
      year_built: data.specs.year_built ?? undefined,
      floor: data.specs.floor ?? undefined,
    } : undefined,
    // Characteristics
    characteristics: data.characteristics ? {
      pool: data.characteristics.pool ?? undefined,
      garden: data.characteristics.garden ?? undefined,
      terrace: data.characteristics.terrace ?? undefined,
      patio: (data.characteristics as any).patio ?? undefined,
      air_conditioning: data.characteristics.air_conditioning ?? undefined,
      elevator: data.characteristics.elevator ?? undefined,
      garage: (data.characteristics as any).garage ?? undefined,
      storage_room: (data.characteristics as any).storage_room ?? undefined,
      balcony: (data.characteristics as any).balcony ?? undefined,
      exterior: (data.characteristics as any).exterior ?? undefined,
      jacuzzi: (data.characteristics as any).jacuzzi ?? undefined,
      sauna: (data.characteristics as any).sauna ?? undefined,
      bbq_area: (data.characteristics as any).bbq_area ?? undefined,
      sea_views: (data.characteristics as any).sea_views ?? undefined,
      heating: data.characteristics.heating ?? undefined,
      orientation: data.characteristics.orientation ?? undefined,
      views: data.characteristics.views ?? undefined,
      energy_rating: data.characteristics.energy_rating ?? undefined,
      furnished: data.characteristics.furnished ?? undefined,
      storage: data.characteristics.storage ?? undefined,
      highlighted_features: data.characteristics.highlighted_features ?? undefined,
    } : undefined,
    featured: data.featured ?? false,
    // Badge
    badge: (data as any).badge || undefined,
  }
}

/**
 * Página de creación de propiedades con implementación completa del formulario.
 * Usa React Hook Form + Zod para validación y Convex mutations.
 */
export function PropertyCreate() {
  const navigate = useNavigate()
  const createMutation = useCreateProperty()
  const saveImagesMutation = useSaveImagesForNewProperty()

  async function handleSubmit(data: PropertyCreateData, stagedImages: StagedImage[]) {
    try {
      const convexData = formDataToConvex(data)
      const propertyId = await createMutation.mutateAsync(convexData)

      // Save staged images if any
      if (stagedImages.length > 0) {
        try {
          await saveImagesMutation.mutateAsync({
            propertyId,
            images: stagedImages,
          })
        } catch (imageError) {
          // Log error but don't fail the whole operation
          // Property is created, images just didn't save
          console.error('Failed to save images:', imageError)
        }
      }

      // Navigate to the properties list after successful creation
      navigate('/properties', {
        state: { message: '¡Propiedad creada exitosamente!' },
      })
    } catch (error) {
      // Error is already captured in createMutation.error
      console.error('Failed to create property:', error)
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      {/* Encabezado */}
      <div>
        <Link
          to="/properties"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          ← Volver a Propiedades
        </Link>
        <h1 className="mt-4 text-3xl font-bold tracking-tight">Crear Propiedad</h1>
        <p className="mt-2 text-muted-foreground">
          Añadir un nuevo listado de propiedad al sistema.
        </p>
      </div>

      {/* Formulario */}
      <PropertyForm
        onSubmit={handleSubmit}
        isSubmitting={createMutation.isPending || saveImagesMutation.isPending}
        submitError={createMutation.error?.message || saveImagesMutation.error?.message}
      />
    </div>
  )
}
